import { api } from './client';
import type {
  AzureConnectionCheck,
  CloudConnectionSummary,
  CloudDisconnectResult,
  CloudStatus,
  GcpConnectStep,
  GcpPricingDatabase,
  GcpPricingEstimate,
  GcpSize,
  GcpListBillingResult,
  GcpListProjectsResult,
  GcpRoleCheckResult,
  ProjectCloud,
  ProjectDeployment,
  ProjectStack,
  SetProjectCloudResponse,
  StackTier,
} from '@mycompbox/shared';

export type { GcpPricingDatabase, GcpPricingEstimate, GcpSize };

/** One item of the background Google Cloud setup. */
export type GcpSetupStepState = 'pending' | 'running' | 'done' | 'skipped' | 'failed';

/** The background Google Cloud setup job's per-item progress (the API's CloudSetupJobView). */
export interface GcpSetupJob {
  id: string;
  projectId: string;
  region: string;
  /** running | succeeded | failed | interrupted */
  status: string;
  steps: Record<string, GcpSetupStepState>;
  /** Why a step was skipped, per step (only skipped steps appear). */
  skipReasons: Record<string, string>;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** What the admin chose after signing in with Google. */
export interface GcpSetupInput {
  session: string;
  projectId: string;
  region: string;
  billingAccountId?: string;
  budgetUsd?: number;
}
export type { CloudConnectionSummary, CloudStatus, ProjectCloud, ProjectDeployment, ProjectStack, SetProjectCloudResponse, StackTier };

export const cloudApi = {
  status: () => api.get<{ data: CloudStatus }>('/integrations/cloud'),
  /** Live Google Cloud prices for a stack in a region; rejects (503) when the catalog is unavailable. */
  gcpPricing: (input: { region: string; database: GcpPricingDatabase; size: GcpSize }) =>
    api.get<{ data: GcpPricingEstimate }>(`/integrations/cloud/pricing/gcp?${new URLSearchParams(input).toString()}`),
  connectGcp: (input: { projectId: string; projectNumber: string; region: string }) =>
    api.put<{ data: CloudConnectionSummary }>('/integrations/cloud/gcp', input),
  /** One-click Google Cloud: Google's sign-in link, then finish with what Google sent back. */
  gcpAuthorize: (input: { projectId?: string; region?: string } = {}) =>
    api.post<{ data: { url: string } }>('/integrations/cloud/gcp/authorize', input),
  gcpStart: (input: { code: string; state: string }) =>
    api.post<{ data: { session: string; projectId?: string; steps: GcpConnectStep[] } }>('/integrations/cloud/gcp/start', input),
  gcpProjects: (input: { session: string }) =>
    api.post<{ data: GcpListProjectsResult }>('/integrations/cloud/gcp/projects', input),
  gcpBilling: (input: { session: string; projectId?: string }) =>
    api.post<{ data: GcpListBillingResult }>('/integrations/cloud/gcp/billing', input),
  gcpPermissions: (input: { session: string; projectId?: string }) =>
    api.post<{ data: GcpRoleCheckResult }>('/integrations/cloud/gcp/permissions', input),
  gcpStep: (input: { session: string; step: GcpConnectStep }) =>
    api.post<{ data: { session: string; again: boolean; connection: CloudConnectionSummary | null } }>('/integrations/cloud/gcp/step', input),
  /** Start the whole setup as a background job; progress survives reloads. */
  gcpSetupStart: (input: GcpSetupInput) => api.post<{ data: GcpSetupJob }>('/integrations/cloud/gcp/setup', input),
  /** The latest setup job's progress, or null when none ran. */
  gcpSetupJob: () => api.get<{ data: GcpSetupJob | null }>('/integrations/cloud/gcp/setup'),
  awsLink: (region: string) => api.post<{ data: { url: string } }>('/integrations/cloud/aws/link', { region }),
  azureLink: () => api.post<{ data: { url: string } }>('/integrations/cloud/azure/link', {}),
  rename: (id: string, name: string) =>
    api.patch<{ data: CloudConnectionSummary }>(`/integrations/cloud/${encodeURIComponent(id)}`, { name }),
  setCap: (id: string, cap: number | null) =>
    api.put<{ data: CloudConnectionSummary }>(`/integrations/cloud/${encodeURIComponent(id)}/cap`, { cap }),
  setDefault: (id: string) =>
    api.post<{ data: CloudConnectionSummary }>(`/integrations/cloud/${encodeURIComponent(id)}/default`, {}),
  azureCheck: (id: string) =>
    api.post<{ data: AzureConnectionCheck }>(`/integrations/cloud/${encodeURIComponent(id)}/azure-check`, {}),
  setBudget: (id: string, budgetUsd: number | null) =>
    api.put<{ data: CloudConnectionSummary }>(`/integrations/cloud/${encodeURIComponent(id)}/budget`, { budgetUsd }),
  remove: (id: string) => api.delete<{ data: CloudDisconnectResult }>(`/integrations/cloud/${encodeURIComponent(id)}`),
};

/** The cloud a project deploys to: its own choice, or the organization's default. */
export const projectCloudApi = {
  get: (teamId: string) => api.get<{ data: ProjectCloud }>(`/teams/${teamId}/cloud`),
  /** Pick one of the organization's clouds, or null to follow its default. */
  set: (teamId: string, cloudConnectionId: string | null) =>
    api.put<{ data: SetProjectCloudResponse }>(`/teams/${teamId}/cloud`, { cloudConnectionId }),
  /** Write the project's Google Cloud keyless facts to its GitHub repository as Actions variables. */
  syncRepo: (teamId: string) => api.post<{ data: RepoVariablesSync }>(`/teams/${teamId}/cloud/sync-repo`, {}),
};

/** One GitHub repository variable's sync outcome. */
export interface RepoVariableResult {
  name: string;
  status: "created" | "updated" | "unchanged" | "clash" | "needs-permission" | "error";
  message?: string;
}

/** The outcome of syncing the project's cloud facts to its repository's GitHub variables. */
export interface RepoVariablesSync {
  status: "synced" | "clash" | "needs-permission" | "skipped" | "error";
  reason?: string;
  repository: string | null;
  variables: RepoVariableResult[];
}

export const stackApi = {
  get: (teamId: string) => api.get<{ data: ProjectStack }>(`/teams/${teamId}/stack`),
  set: (teamId: string, tier: StackTier) => api.put<{ data: ProjectStack }>(`/teams/${teamId}/stack`, { tier }),
  /** Starts tearing down stage or prod; `confirm` must be "<app>-<environment>". */
  teardown: (teamId: string, environment: string, confirm: string) =>
    api.post<{ data: TeardownStarted }>(`/teams/${teamId}/teardown`, { environment, confirm }),
};

/** A teardown that GitHub has queued. */
export interface TeardownStarted {
  environment: string;
  status: "queued";
  /** Where to watch the teardown run in GitHub Actions. */
  url: string;
}

export const deploymentApi = {
  list: (teamId: string) => api.get<{ data: ProjectDeployment[] }>(`/teams/${teamId}/deployments`),
  start: (teamId: string, environment: string) =>
    api.post<{ data: ProjectDeployment }>(`/teams/${teamId}/deployments`, { environment }),
};
